// src/pages/TeamAndReferrals.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

const safe = (v, fb = "") => (v === null || v === undefined ? fb : String(v));

const normalizePos = (p) => {
  const s = String(p || "").trim().toUpperCase();
  if (s === "L" || s === "LEFT") return "LEFT";
  if (s === "R" || s === "RIGHT") return "RIGHT";
  return s || "";
};

export default function TeamAndReferrals() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);

  const [leftRef, setLeftRef] = useState(null);
  const [rightRef, setRightRef] = useState(null);

  const [miniOpen, setMiniOpen] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);
  const [directsOpen, setDirectsOpen] = useState(false);
  const [shareData, setShareData] = useState({ pos: "", url: "", code: "" });

  const token = useMemo(
    () => localStorage.getItem("token") || localStorage.getItem("authToken"),
    []
  );

  const safeNum = (v) => {
    const n = Number(v || 0);
    return Number.isFinite(n) ? n : 0;
  };

  const toast = (msg) => {
    setOk(msg);
    window.clearTimeout(window.__t_ok__team);
    window.__t_ok__team = window.setTimeout(() => setOk(""), 1700);
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ""));
      toast("✅ Copied!");
    } catch {
      // fallback for HTTP / older browsers
      try {
        const el = document.createElement("textarea");
        el.value = String(text || "");
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        toast("✅ Copied!");
      } catch {
        toast("❌ Copy failed — please copy manually");
      }
    }
  };

  const openUrl = (u) => {
    const url = safe(u);
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const waShareLink = ({ pos, code, url }) => {
    const text = `Join using my referral ✅\n\nSide: ${pos}\nReferral Code: ${code}\nLink: ${url}`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  const closeAll = () => {
    setMiniOpen(false);
    setFullOpen(false);
    setDirectsOpen(false);
  };

  const openMini = (pos, item) => {
    const code = safe(item?.code || item?.referralCode, "");
    const url = safe(item?.url, "");
    setShareData({ pos, code, url });
    setMiniOpen(true);
    setFullOpen(false);
  };

  const openFull = () => {
    setMiniOpen(false);
    setFullOpen(true);
  };

  const fetchReferrals = async () => {
    const res = await axiosInstance.get("/api/referrals");
    const data = res?.data;

    const arr = Array.isArray(data)
      ? data
      : Array.isArray(data?.referrals)
        ? data.referrals
        : Array.isArray(data?.data)
          ? data.data
          : [];

    const sorted = [...arr].sort(
      (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0)
    );

    const pick = (posWanted) =>
      sorted.find((x) => normalizePos(x?.position || x?.side || x?.pos) === posWanted) ||
      null;

    setLeftRef(pick("LEFT"));
    setRightRef(pick("RIGHT"));
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      setErr("");
      setOk("");

      const [meRes, statsRes, summaryRes] = await Promise.all([
        axiosInstance.get("/api/users/me").catch(() => null),
        axiosInstance.get("/api/binary/stats").catch(() => null),
        axiosInstance.get("/api/reports/referral-summary").catch(() => null),
      ]);

      const u = meRes?.data?.user || meRes?.data || null;
      setMe(u);

      setStats(statsRes?.data || null);

      const sumData = summaryRes?.data?.summary || summaryRes?.data || null;
      setSummary(sumData);

      try {
        await fetchReferrals();
      } catch {
        setLeftRef(null);
        setRightRef(null);
      }
    } catch (e) {
      setMe(null);
      setStats(null);
      setSummary(null);
      setLeftRef(null);
      setRightRef(null);
      setErr(
        e?.response?.data?.msg ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ======= COUNTS =======
  const leftTotal = safeNum(stats?.left?.TOTAL);
  const leftActive = safeNum(stats?.left?.ENTREPRENEUR);
  const leftInactive = safeNum(stats?.left?.TRAINEE_ENTREPRENEUR);
  const leftCarryForward = safeNum(stats?.leftCarryForward);

  const rightTotal = safeNum(stats?.right?.TOTAL);
  const rightActive = safeNum(stats?.right?.ENTREPRENEUR);
  const rightInactive = safeNum(stats?.right?.TRAINEE_ENTREPRENEUR);
  const rightCarryForward = safeNum(stats?.rightCarryForward);

  const overallTotal = safeNum(stats?.overall?.TOTAL);
  const overallActive = safeNum(stats?.overall?.ENTREPRENEUR);
  const overallInactive = safeNum(stats?.overall?.TRAINEE_ENTREPRENEUR);

  const directTotal = safeNum(stats?.direct?.TOTAL);
  const totalPairs = safeNum(stats?.totalPairs);

  const sDay = safeNum(summary?.day);
  const sWeek = safeNum(summary?.week);
  const sMonth = Math.max(safeNum(summary?.month), sWeek);
  const sYear = Math.max(safeNum(summary?.year), sMonth);

  const directsList = Array.isArray(stats?.directReferralsList) ? stats.directReferralsList : [];

  // ─── Referral Inline ────────────────────────────────────────────────────────
  const RefInline = ({ posLabel, item, tone }) => {
    const url = safe(item?.url, "");
    const code = safe(item?.code || item?.referralCode, "");
    const pos = posLabel.toUpperCase();

    return (
      <div style={S.refInlineWrap}>
        <div style={S.refInlineTop}>
          <div style={S.refInlineTitle}>
            {posLabel} Referral Link{" "}
            <span style={tone === "left" ? S.badgeLeft : S.badgeRight}>{pos}</span>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              style={tone === "left" ? S.btnLeft : S.btnRight}
              disabled={!url || loading}
              onClick={() => url && copyText(url)}
            >
              Copy
            </button>
            <button
              type="button"
              style={S.btnGhost}
              disabled={!url || loading}
              onClick={() => url && openMini(pos, item)}
            >
              Share
            </button>
          </div>
        </div>

        <input
          style={S.refInlineInput}
          value={url}
          readOnly
          placeholder={loading ? "Loading..." : "No link found"}
          onClick={() => url && openMini(pos, item)}
        />
      </div>
    );
  };

  // ─── Stat Row ───────────────────────────────────────────────────────────────
  const StatRow = ({ label, value }) => (
    <div style={S.statRow}>
      <div style={S.statLabel}>{label}</div>
      <div style={S.statValue}>{value}</div>
    </div>
  );

  // ─── Group Card ─────────────────────────────────────────────────────────────
  const GroupCard = ({ title, tone, total, active, inactive, pairs, carry, refItem }) => {
    const headStyle = tone === "left" ? S.headLeft : S.headRight;

    return (
      <div style={S.card}>
        <div style={{ ...S.cardHead, ...headStyle }}>{title}</div>

        <div style={S.cardBody}>
          {/* ✅ Total B.V. removed — replaced with Members labels */}
          <StatRow label="Total Team Count :" value={total} />
          <StatRow label="Entrepreneur Team Count :" value={active} />
          <StatRow label="Trainee Entrepreneur Team Count :" value={inactive} />
          <StatRow label="Payout Paid Members :" value={pairs} />
          <StatRow label="Carry Forward Members :" value={carry} />

          <div style={{ marginTop: 12 }}>
            <RefInline
              posLabel={tone === "left" ? "Left" : "Right"}
              item={refItem}
              tone={tone}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={S.page}>
      <style>{css}</style>

      <div style={S.container}>
        {loading ? <div style={S.toast}>Loading…</div> : null}
        {err ? <div style={{ ...S.toast, ...S.toastErr }}>{err}</div> : null}
        {ok ? (
          <div style={{
            position: "fixed",
            bottom: 80,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#166534",
            color: "#fff",
            padding: "12px 28px",
            borderRadius: 14,
            fontWeight: 800,
            fontSize: 15,
            zIndex: 99999,
            boxShadow: "0 8px 24px rgba(0,0,0,.22)",
            whiteSpace: "nowrap",
            pointerEvents: "none",
            border: "1px solid rgba(34,197,94,.35)",
          }}>
            {ok}
          </div>
        ) : null}
        {!loading ? (
          <>
            {/* ── Top 2 summary boxes ── */}
            <div style={S.twoBoxGrid}>
              <div style={{ ...S.imgCard, background: "linear-gradient(180deg, #b05c89 0%, #d412ed 100%)" }}>
                <div style={S.imgCardTop}>
                  <div style={S.imgCardTitle}>Team Total</div>
                  <div
                    style={{ ...S.imgCardBadge, cursor: "pointer", border: "1px solid rgba(255,255,255,0.4)" }}
                    onClick={() => setDirectsOpen(true)}
                    title="Click to view your directs"
                  >
                    My Directs : {directTotal}
                  </div>
                </div>
                <div style={S.imgCardMiddle}>
                  {leftActive} / {rightActive} / {overallTotal}
                </div>
                <div style={S.imgCardBottom}>
                  <div style={S.imgCardSubText}>Team (Left / Right / Total)</div>
                  <div style={S.imgCardSmallVal}>{directTotal} ↑</div>
                </div>
              </div>

              <div style={{ ...S.imgCard, background: "linear-gradient(180deg, #18987d 0%, #30df61 100%)" }}>
                <div style={S.imgCardTop}>
                  <div style={S.imgCardTitle}>Day / Week / Month / Year</div>
                </div>
                <div style={S.imgCardMiddle}>
                  {sDay} / {sWeek} / {sMonth} / {sYear}
                </div>
                <div style={S.imgCardBottom}>
                  <div style={S.imgCardSubText}>Day / Weekly / Monthly / Yearly</div>
                </div>
              </div>
            </div>

            {/* ── User ID + overall pills ── */}
            <div style={S.topBox}>
              <div style={S.userId}>{safe(me?.userID, "—")}</div>
              <div style={S.pills}>
                <span style={S.pill}>TOTAL: {overallTotal}</span>
                <span style={{ ...S.pill, background: "#16a34a", color: "#fff", borderColor: "#15803d" }}>ACTIVE: {overallActive}</span>
                <span style={{ ...S.pill, background: "#ef4444", color: "#fff", borderColor: "#b91c1c" }}>INACTIVE: {overallInactive}</span>
              </div>
            </div>

            {/* ── Left / Right group cards ── */}
            <div style={S.stack}>
              <GroupCard
                title="Primary Group (Left)"
                tone="left"
                total={leftTotal}
                active={leftActive}
                inactive={leftInactive}
                pairs={totalPairs}
                carry={leftCarryForward}
                refItem={leftRef}
              />

              <GroupCard
                title="Secondary Group (Right)"
                tone="right"
                total={rightTotal}
                active={rightActive}
                inactive={rightInactive}
                pairs={totalPairs}
                carry={rightCarryForward}
                refItem={rightRef}
              />
            </div>
          </>
        ) : null}
      </div>

      {/* ── Mini share popup ── */}
      {miniOpen && (
        <div style={S.popBackdrop} onClick={closeAll} role="presentation">
          <div
            style={S.popMini}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div style={S.popTop}>
              <div style={S.popTitle}>Share</div>
              <button style={S.popX} type="button" onClick={closeAll}>✕</button>
            </div>
            <div style={S.popBtns}>
              <button style={S.popBtn} type="button" onClick={() => copyText(shareData.url)}>
                Copy Link
              </button>
              <a
                style={{ ...S.popBtn, ...S.popBtnWA }}
                href={waShareLink(shareData)}
                target="_blank"
                rel="noreferrer"
              >
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── Full share popup ── */}
      {fullOpen && (
        <div style={S.popBackdrop} onClick={closeAll} role="presentation">
          <div
            style={S.popModal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div style={S.popHead}>
              <div style={S.popTitle2}>Share Options</div>
              <button style={S.popX} type="button" onClick={closeAll}>✕</button>
            </div>
            <div style={S.popBody}>
              <div style={{ ...S.popRow, gridTemplateColumns: "120px 1fr" }}>
                <div style={S.popK}>Side</div>
                <div style={S.popV}>{safe(shareData.pos, "-")}</div>
              </div>
              <div style={S.popRow}>
                <div style={S.popK}>Referral Code</div>
                <div style={{ ...S.popV, ...S.mono }}>{safe(shareData.code, "-")}</div>
                <button style={S.popSmallBtn} type="button" onClick={() => copyText(shareData.code)}>
                  Copy
                </button>
              </div>
              <div style={S.popRow}>
                <div style={S.popK}>Link</div>
                <div style={{ ...S.popV, ...S.mono }}>{safe(shareData.url, "-")}</div>
                <button style={S.popSmallBtn} type="button" onClick={() => copyText(shareData.url)}>
                  Copy
                </button>
              </div>
              <div style={S.popActions}>
                <button
                  style={S.popPrimaryBtn}
                  type="button"
                  onClick={() => openUrl(shareData.url)}
                  disabled={!shareData.url}
                >
                  Open Link
                </button>
                <a
                  style={{ ...S.popPrimaryBtn, ...S.popPrimaryWA }}
                  href={waShareLink(shareData)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Share WhatsApp
                </a>
                <button
                  style={S.popGhostBtn}
                  type="button"
                  onClick={async () => {
                    const text = `Join using my referral ✅\n\nSide: ${shareData.pos}\nReferral Code: ${shareData.code}\nLink: ${shareData.url}`;
                    if (navigator.share) {
                      try {
                        await navigator.share({
                          title: "Referral Link",
                          text,
                          url: shareData.url || undefined,
                        });
                      } catch {
                        // ignore
                      }
                    } else {
                      await copyText(text);
                    }
                  }}
                >
                  Share (Mobile)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── Directs List popup ── */}
      {directsOpen && (
        <div style={S.popBackdrop} onClick={closeAll} role="presentation">
          <div
            style={S.popModal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div style={S.popHead}>
              <div style={S.popTitle2}>My Direct Referrals</div>
              <button style={S.popX} type="button" onClick={closeAll}>✕</button>
            </div>
            <div style={{ padding: 14, maxHeight: "60vh", overflowY: "auto", background: "#f8fafc" }}>
              {directsList.length === 0 ? (
                <div style={{ textAlign: "center", padding: 20, color: "#64748b", fontWeight: 700 }}>
                  No directs found.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  {directsList.map((usr) => (
                    <div key={usr.id} style={{
                      background: "#fff",
                      border: "1px solid rgba(15,23,42,.10)",
                      borderRadius: 12,
                      padding: 12,
                      display: "flex",
                      flexDirection: "column",
                      gap: 4
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 950, fontSize: 16, color: "#0f172a" }}>{safe(usr.name, "—")}</span>
                        <span style={{
                          fontSize: 10, fontWeight: 900, padding: "3px 8px", borderRadius: 999,
                          background: usr.userType === "ENTREPRENEUR" ? "#16a34a" : "#ef4444",
                          color: "#ffffff"
                        }}>
                          {usr.userType === "ENTREPRENEUR" ? "Entrepreneur" : "Trainee"}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: "#475569", fontWeight: 800 }}>ID: <span style={{ color: "#2563eb" }}>{usr.userID}</span></div>
                      {usr.joinedAt && (
                        <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>
                          Joined: {new Date(usr.joinedAt).toLocaleDateString()} {new Date(usr.joinedAt).toLocaleTimeString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── CSS ─────────────────────────────────────────────────────────────────── */
const css = `
  *{ margin:0; padding:0; box-sizing:border-box; }
  html,body,#root{ height:100%; width:100%; }
  body{
    overflow-x:hidden;
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
    background: #ffffff;
    color:#0f172a;
  }
  button, input{ font-family: inherit; }
`;

/* ─── Styles ──────────────────────────────────────────────────────────────── */
const S = {
  page: { minHeight: "100vh" },
  container: { width: "min(860px, 100%)", margin: "0 auto", padding: "14px 14px 70px" },

  toast: {
    padding: "12px 14px",
    borderRadius: 14,
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,.10)",
    boxShadow: "0 10px 24px rgba(15,23,42,.06)",
    marginBottom: 10,
    color: "#0f172a",
  },
  toastErr: {
    borderColor: "rgba(239,68,68,.28)",
    background: "rgba(239,68,68,.08)",
    color: "#991b1b",
  },
  toastOk: {
    borderColor: "rgba(34,197,94,.28)",
    background: "rgba(34,197,94,.08)",
    color: "#166534",
  },

  // ── Top 2 summary boxes ──
  twoBoxGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: 16,
    marginBottom: 16,
  },
  imgCard: {
    borderRadius: 4,
    color: "#fff",
    padding: "16px 20px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    minHeight: 130,
  },
  imgCardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  imgCardTitle: { fontSize: 18, fontWeight: 400, letterSpacing: 0.5 },
  imgCardBadge: {
    background: "#2563ea",
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
  },
  imgCardMiddle: { fontSize: 26, fontWeight: "300", marginBottom: 8, letterSpacing: 1 },
  imgCardBottom: { display: "flex", justifyContent: "space-between", alignItems: "flex-end" },
  imgCardSubText: { fontSize: 12, fontWeight: 400, opacity: 0.9 },
  imgCardSmallVal: { fontSize: 14, fontWeight: 600 },

  // ── User ID box ──
  topBox: {
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,.10)",
    background: "#ffffff",
    boxShadow: "0 10px 24px rgba(15,23,42,.06)",
    padding: 14,
    textAlign: "center",
    marginBottom: 12,
  },
  userId: { fontSize: 28, fontWeight: 950, color: "#0f172a", letterSpacing: 0.3 },
  pills: { display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 10 },
  pill: {
    padding: "8px 12px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#f8fafc",
    fontWeight: 950,
    fontSize: 12,
    color: "#0f172a",
  },
  pillActive: {
    borderColor: "rgba(34,197,94,.35)",
    background: "rgba(34,197,94,.10)",
    color: "#166534",
  },
  pillInactive: {
    borderColor: "rgba(239,68,68,.35)",
    background: "rgba(239,68,68,.10)",
    color: "#991b1b",
  },

  // ── Group cards ──
  stack: { display: "grid", gap: 14 },

  card: {
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,.12)",
    overflow: "hidden",
    background: "#ffffff",
    boxShadow: "0 10px 24px rgba(15,23,42,.06)",
  },
  cardHead: { padding: "12px 16px", fontSize: 16, fontWeight: 950, color: "#fff" },
  headLeft: { background: "#16a34a" },
  headRight: { background: "#ef4444" },

  cardBody: { padding: "12px 12px 14px", background: "#ffffff" },

  // ── Stat rows — one below the other ──
  statRow: {
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,.10)",
    borderRadius: 12,
    padding: "13px 14px",
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: 10,
    marginBottom: 10,
    boxShadow: "0 4px 10px rgba(15,23,42,.04)",
  },
  statLabel: { fontSize: 14, fontWeight: 900, color: "#0f172a" },
  statValue: { fontSize: 18, fontWeight: 950, color: "#0f172a", minWidth: 40, textAlign: "right" },

  // ── Referral inline ──
  refInlineWrap: {
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,.10)",
    background: "#f8fafc",
    padding: 12,
  },
  refInlineTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  refInlineTitle: { fontSize: 13, fontWeight: 950, color: "#0f172a" },
  badgeLeft: {
    marginLeft: 8,
    padding: "3px 10px",
    borderRadius: 999,
    border: "1px solid rgba(22,163,74,.35)",
    background: "rgba(22,163,74,.12)",
    color: "#166534",
    fontSize: 12,
    fontWeight: 950,
  },
  badgeRight: {
    marginLeft: 8,
    padding: "3px 10px",
    borderRadius: 999,
    border: "1px solid rgba(239,68,68,.35)",
    background: "rgba(239,68,68,.12)",
    color: "#991b1b",
    fontSize: 12,
    fontWeight: 950,
  },
  refInlineInput: {
    marginTop: 10,
    width: "100%",
    height: 44,
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#ffffff",
    color: "#0f172a",
    padding: "0 12px",
    outline: "none",
    cursor: "pointer",
    fontSize: 13,
  },

  mono: {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },

  btnLeft: {
    height: 36, padding: "0 14px", borderRadius: 12,
    border: "1px solid rgba(22,163,74,.35)",
    background: "rgba(22,163,74,.12)",
    color: "#166534", fontWeight: 950, cursor: "pointer",
  },
  btnRight: {
    height: 36, padding: "0 14px", borderRadius: 12,
    border: "1px solid rgba(239,68,68,.35)",
    background: "rgba(239,68,68,.12)",
    color: "#991b1b", fontWeight: 950, cursor: "pointer",
  },
  btnGhost: {
    height: 36, padding: "0 14px", borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#ffffff",
    color: "#0f172a", fontWeight: 950, cursor: "pointer",
  },
  btnGhostSmall: {
    height: 32, padding: "0 10px", borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#ffffff",
    color: "#0f172a", fontWeight: 950, cursor: "pointer",
  },

  // ── Popups ──
  popBackdrop: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
    display: "flex", alignItems: "flex-end", justifyContent: "center",
    padding: 12, zIndex: 9999,
  },
  popMini: {
    width: "min(520px, 100%)", background: "#ffffff",
    borderRadius: 18, overflow: "hidden",
    boxShadow: "0 20px 60px rgba(0,0,0,.30)", padding: 12,
  },
  popTop: {
    display: "flex", alignItems: "center",
    justifyContent: "space-between", padding: "6px 4px 10px",
  },
  popTitle: { fontSize: 16, fontWeight: 950, color: "#0f172a" },
  popX: {
    width: 40, height: 40, borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#ffffff", cursor: "pointer", fontWeight: 950,
  },
  popBtns: { display: "grid", gridTemplateColumns: "1fr", gap: 10 },
  popBtn: {
    height: 46, borderRadius: 14,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#ffffff", cursor: "pointer", fontWeight: 950,
    display: "flex", alignItems: "center", justifyContent: "center",
    textDecoration: "none", color: "#0f172a",
  },
  popBtnPrimary: { border: "none", background: "rgba(37,99,235,.12)", color: "#1e3a8a" },
  popBtnWA: { border: "none", background: "#16a34a", color: "#ffffff" },

  popModal: {
    width: "min(720px, 100%)", background: "#ffffff",
    borderRadius: 18, overflow: "hidden",
    boxShadow: "0 20px 60px rgba(0,0,0,.30)",
  },
  popHead: {
    padding: 14, display: "flex", alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid rgba(15,23,42,.10)",
  },
  popTitle2: { fontSize: 18, fontWeight: 950, color: "#0f172a" },
  popBody: { padding: 14 },
  popRow: {
    display: "grid", gridTemplateColumns: "120px 1fr 90px",
    gap: 10, alignItems: "center",
    border: "1px solid rgba(15,23,42,.10)",
    borderRadius: 14, padding: 10, marginTop: 10, background: "#f8fafc",
  },
  popK: { fontWeight: 950, color: "#334155", fontSize: 13 },
  popV: { fontWeight: 900, color: "#0f172a", fontSize: 13, wordBreak: "break-all" },
  popSmallBtn: {
    height: 40, borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#ffffff", cursor: "pointer", fontWeight: 950,
  },
  popActions: { display: "grid", gap: 10, marginTop: 14 },
  popPrimaryBtn: {
    height: 48, borderRadius: 14, border: "none",
    cursor: "pointer", fontWeight: 950, color: "#ffffff",
    background: "#2563eb", textDecoration: "none",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  popPrimaryWA: { background: "#16a34a" },
  popGhostBtn: {
    height: 48, borderRadius: 14,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#ffffff", cursor: "pointer", fontWeight: 950,
  },
};
